import { useAuth0 } from '@auth0/auth0-react';
import React, { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { LoadingLogo } from '@fmbt/ui/LoadingSpinner';
import Modal from '@fmbt/ui/Modal';
import Text from '@fmbt/ui/Text';
import HelmetWrapper from 'components/atoms/HelmetWrapper';
import { logout as logoutAction } from 'store/modules/auth/actions';
import { logoutAnnouncingToNative } from 'utils/native-container/logout';
import {
  getInvitePostLogoutReturnToUrl,
  stashInvitePostLogoutReturnPath
} from 'utils/URL/invite-post-logout-return-path';
import { ENTERPRISE_LIST_PATH } from 'utils/URL/switch-enterprises';

type Props = {
  invitationEmail: string;
};

function hasSessionEmailMatch(sessionEmail: string | undefined, invitationEmail: string): boolean {
  return !!sessionEmail
    && sessionEmail.toLowerCase() === invitationEmail.toLowerCase();
}

function maskEmail(email: string | undefined): string {
  if (!email) {
    return 'unknown email';
  }
  if (!email.includes('@')) {
    return 'invalid email';
  }
  // "local-part@domain" is the standard format for email addresses (RFC 5322)
  const [localPart, domain] = email.split('@');
  if (!localPart || !domain) {
    return 'invalid email';
  }
  const maskedLocalPart = localPart.length <= 1
    ? localPart
    : `${localPart.charAt(0)}${'*'.repeat(localPart.length - 1)}`;
  return `${maskedLocalPart}@${domain}`;
}

export default function ExistingSessionPrompt({ invitationEmail }: Props): JSX.Element {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { logout: auth0Logout, user: auth0User } = useAuth0();
  const hasEmailMatch = hasSessionEmailMatch(auth0User?.email, invitationEmail);

  const navigateToEnterpriseList = useCallback(() => {
    navigate(ENTERPRISE_LIST_PATH, { replace: true });
  }, [navigate]);

  useEffect(() => {
    if (hasEmailMatch) {
      navigateToEnterpriseList(); // And display an interim loading message (see the render part below)
    }
  }, [hasEmailMatch, navigateToEnterpriseList]);

  const handleContinueSession = useCallback(() => {
    navigateToEnterpriseList();
  }, [navigateToEnterpriseList]);

  const handleLogout = useCallback(() => {
    stashInvitePostLogoutReturnPath(pathname);
    logoutAnnouncingToNative(auth0Logout, {
      logoutParams: { returnTo: getInvitePostLogoutReturnToUrl() }
    });
    dispatch(logoutAction());
  }, [auth0Logout, dispatch, pathname]);

  if (hasEmailMatch) {
    return <LoadingLogo subtitle="Redirecting to accept invitation..." />;
  }

  const maskedSessionEmail = maskEmail(auth0User?.email);
  const maskedInvitationEmail = maskEmail(invitationEmail);

  return (
    <>
      <HelmetWrapper section="Invitation Mismatch" />
      <Modal
        confirmText="Log out and accept invitation"
        dismissText="Ignore invitation"
        hasStackedActions
        isOpen
        maskClosable={false}
        title="Invitation Mismatch"
        onConfirm={handleLogout}
        onDismiss={handleContinueSession}
      >
        <Text v="body">
          You are currently logged in as {maskedSessionEmail}. The invitation was sent to{' '}
          {maskedInvitationEmail}. To accept this invite, you need to log out of the current account
          and continue as {maskedInvitationEmail}.
        </Text>
      </Modal>
    </>
  );
}
